import React, { useState, useRef, useCallback, useEffect } from 'react';
import {
  Box, Modal, TextField, Button, IconButton, CircularProgress, Typography, Autocomplete
} from '@mui/material';
import { debounce } from 'lodash';
import CloseIcon from '@mui/icons-material/Close';
import { useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import ErrorManager from './ErrorManager';

const DuplicateModals = ({ 
  duplicateModalOpen, 
  duplicateWabaModalOpen, 
  duplicateBrokerModalOpen,
  duplicateDateTime, 
  setDuplicateDateTime,
  isDuplicating,
  wabaTemplates,
  selectedTemplate,
  setSelectedTemplate,
  // Props específicas do BROKER
  brokerMensagem,
  setBrokerMensagem,
  brokerMensagemRapida,
  setBrokerMensagemRapida,
  brokerMensagensRapidas,
  brokerImagem,
  setBrokerImagem,
  brokerImagemUrl,
  setBrokerImagemUrl,
  onClose,
  onConfirmDuplicate,
  onConfirmDuplicateWaba,
  onConfirmDuplicateBroker,
  wabaChannelId,
  brokerChannelId
}) => {
  const { acontID, token } = useParams();
  const { t } = useTranslation();
  const errorManagerRef = useRef(null);
  const [isImageUploading, setIsImageUploading] = useState(false);
  
  // Estados para busca de templates WABA
  const [wabaTemplateSearchOpen, setWabaTemplateSearchOpen] = useState(false);
  const [wabaTemplateSearchTerm, setWabaTemplateSearchTerm] = useState('');
  const [wabaTemplatesList, setWabaTemplatesList] = useState(wabaTemplates);
  const [isLoadingWabaTemplates, setIsLoadingWabaTemplates] = useState(false);
  
  // Estados para busca de mensagens BROKER
  const [brokerSearchOpen, setBrokerSearchOpen] = useState(false);
  const [brokerSearchTerm, setBrokerSearchTerm] = useState('');
  const [brokerMessagesList, setBrokerMessagesList] = useState(brokerMensagensRapidas);
  const [isLoadingBrokerMessages, setIsLoadingBrokerMessages] = useState(false);

  // Sincronizar templates iniciais quando as modais abrem
  useEffect(() => {
    setWabaTemplatesList(wabaTemplates);
  }, [wabaTemplates, duplicateWabaModalOpen]);

  useEffect(() => {
    setBrokerMessagesList(brokerMensagensRapidas);
  }, [brokerMensagensRapidas, duplicateBrokerModalOpen]);

  const formatDate = (date) => {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    const hours = String(date.getHours()).padStart(2, '0');
    const minutes = String(date.getMinutes()).padStart(2, '0');
    const seconds = String(date.getSeconds()).padStart(2, '0');
    return `${year}-${month}-${day} ${hours}:${minutes}:${seconds}`;
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const debouncedSearchWabaTemplates = useCallback(
    debounce(async (searchTerm) => {
      // Se o termo está vazio, apenas retorna a lista original sem fazer requisição
      if (!searchTerm.trim()) {
        setWabaTemplateSearchTerm('');
        setWabaTemplatesList(wabaTemplates);
        return;
      }

      // Só faz a requisição se o termo tem conteúdo
      setWabaTemplateSearchTerm(searchTerm);
      setIsLoadingWabaTemplates(true);
      
      try {
        const url = `${process.env.REACT_APP_URL_DISP}/get-quick-messages/customer/${acontID}?channel_id=${wabaChannelId}&is_template=true&quick_message_name=${encodeURIComponent(searchTerm)}&perPage=150&page=1`;
        const response = await fetch(url, {
          headers: { 'Authorization': `Bearer ${token}` }
        });
        if (response.ok) {
          const data = await response.json();
          setWabaTemplatesList(data.data || []);
        }
      } catch (error) {
        console.error('Erro ao buscar templates WABA:', error);
      } finally {
        setIsLoadingWabaTemplates(false);
      }
    }, 500),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    []
  );

  // Função de busca para mensagens BROKER
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const debouncedSearchBrokerMessages = useCallback(
    debounce(async (searchTerm) => {
      // Se o termo está vazio, apenas retorna a lista original sem fazer requisição
      if (!searchTerm.trim()) {
        setBrokerSearchTerm('');
        setBrokerMessagesList(brokerMensagensRapidas);
        return;
      }

      // Só faz a requisição se o termo tem conteúdo
      setBrokerSearchTerm(searchTerm);
      setIsLoadingBrokerMessages(true);
      
      try {
        const url = `${process.env.REACT_APP_URL_DISP}/get-quick-messages/customer/${acontID}?channel_id=${brokerChannelId}&is_template=false&quick_message_name=${encodeURIComponent(searchTerm)}&perPage=150&page=1`;
        const response = await fetch(url, {
          headers: { 'Authorization': `Bearer ${token}` }
        });
        if (response.ok) {
          const data = await response.json();
          setBrokerMessagesList(data.data || []);
        }
      } catch (error) {
        console.error('Erro ao buscar mensagens BROKER:', error);
      } finally {
        setIsLoadingBrokerMessages(false);
      }
    }, 500),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    []
  );

  const getButtonText = () => {
    const now = new Date();
    const selected = new Date(duplicateDateTime.replace(' ', 'T'));
    // Se a data for até 5 minutos maior que agora, exibe 'Disparar agora'
    if (selected > now && selected.getTime() - now.getTime() <= 5 * 60 * 1000) {
      return t('duplicate.dispatchNow');
    } else if (selected > now) {
      return t('duplicate.scheduleDispatch');
    } else {
      return t('duplicate.dispatchNow');
    }
  };

  // Funções de upload para BROKER

  const handleImageUpload = async (file) => {
    setIsImageUploading(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      
      const response = await fetch(`${process.env.REACT_APP_URL_DISP}/upload/customer/${acontID}`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` },
        body: formData,
      });
      
      if (response.ok) {
        const result = await response.json();
        return result.fileUrl;
      } else {
        throw new Error('Erro ao duplicar campanha');
      }
    } catch (error) {
      errorManagerRef.current?.showError(t('duplicate.error.imageUpload') + ' ' + error.message);
      return null;
    } finally {
      setIsImageUploading(false);
    }
  };


  const handleBrokerImageChange = async (event) => {
    if (event.target.files && event.target.files[0]) {
      const file = event.target.files[0];
      const fileType = file.type;
      if (fileType === 'image/jpeg' || fileType === 'image/png' || fileType === 'image/gif' || fileType === 'application/pdf') {
        setBrokerImagem(file);
        const url = await handleImageUpload(file);
        if (url) {
          setBrokerImagemUrl(url);
        }
      } else {
        errorManagerRef.current?.showError(t('duplicate.error.invalidImageFormat'));
      }
    }
  };

  return (
    <>
      <ErrorManager ref={errorManagerRef} />
      
      {/* Modal de Duplicação Regular */}
      <Modal
        open={duplicateModalOpen}
        onClose={() => onClose('regular')}
        aria-labelledby="modal-duplicate-confirmation"
        aria-describedby="modal-duplicate-confirmation-description"
      >
        <div className="modal">
          <div
            className="modal-content"
            style={{
              width: '600px',
              maxHeight: '300px',
              top: '50%',
              left: '50%',
              transform: 'translate(-50%, -50%)',
              borderRadius: '10px',
              backgroundColor: 'white',
              boxShadow: '0 4px 8px rgba(0, 0, 0, 0.2)',
              padding: '20px',
              position: 'absolute',
            }}
          >
            <Box display="flex" justifyContent="center" alignItems="center" mb={2} position="relative">
              <Typography id="modal-duplicate-confirmation" variant="h6" component="h2">
                {t('duplicate.title')}
              </Typography>
              <IconButton onClick={() => onClose('regular')} sx={{ position: 'absolute', right: 0 }}>
                <CloseIcon />
              </IconButton>
            </Box>
            <Typography id="modal-duplicate-confirmation-description" variant="body1" mb={2}>
              {t('duplicate.selectDateTime')}
            </Typography>
            <TextField
              fullWidth
              label={t('disparador.dataEnvio')}
              margin="normal"
              type="datetime-local"
              value={duplicateDateTime.slice(0, 16)}
              onChange={(event) => {
                const selectedDate = new Date(event.target.value);
                setDuplicateDateTime(formatDate(selectedDate));
              }}
              InputLabelProps={{ shrink: true }}
              required
            />
            <Box display="flex" justifyContent="flex-end" gap="16px" mt={2}>
              <Button
                variant="outlined"
                onClick={() => onClose('regular')}
                sx={{
                  minWidth: '120px',
                  height: '36px',
                  borderColor: '#1976d2',
                  color: '#1976d2',
                  '&:hover': {
                    borderColor: '#1565c0',
                    backgroundColor: 'rgba(25, 118, 210, 0.04)',
                  },
                }}
              >
                {t('disparador.cancelar')}
              </Button>
              <Button
                variant="contained"
                onClick={onConfirmDuplicate}
                disabled={isDuplicating}
                sx={{
                  minWidth: '120px',
                  height: '36px',
                  backgroundColor: '#1976d2',
                  color: 'white',
                  '&:hover': {
                    backgroundColor: '#1565c0',
                  },
                }}
              >
                {isDuplicating ? (
                  <CircularProgress size={24} style={{ color: 'white' }} />
                ) : (
                  getButtonText()
                )}
              </Button>
            </Box>
          </div>
        </div>
      </Modal>

      {/* Modal de Duplicação WABA */}
      <Modal
        open={duplicateWabaModalOpen}
        onClose={() => onClose('waba')}
        aria-labelledby="modal-duplicate-waba-confirmation"
        aria-describedby="modal-duplicate-waba-confirmation-description"
      >
        <div className="modal">
          <div
            className="modal-content"
            style={{
              width: '600px',
              maxHeight: '500px',
              top: '50%',
              left: '50%',
              transform: 'translate(-50%, -50%)',
              borderRadius: '10px',
              backgroundColor: 'white',
              boxShadow: '0 4px 8px rgba(0, 0, 0, 0.2)',
              padding: '20px',
              position: 'absolute',
            }}
          >
            <Box display="flex" justifyContent="center" alignItems="center" mb={2} position="relative">
              <Typography id="modal-duplicate-waba-confirmation" variant="h6" component="h2">
                {t('duplicate.title')}
              </Typography>
              <IconButton onClick={() => onClose('waba')} sx={{ position: 'absolute', right: 0 }}>
                <CloseIcon />
              </IconButton>
            </Box>
            
            <Typography variant="body1" mb={2}>
              {t('duplicate.configureData')}
            </Typography>

            <Autocomplete
              fullWidth
              open={wabaTemplateSearchOpen}
              onOpen={() => setWabaTemplateSearchOpen(true)}
              onClose={() => {
                setWabaTemplateSearchOpen(false);
                // Resetar o termo de busca quando fechar, retornando à lista original
                if (wabaTemplateSearchTerm.trim()) {
                  setWabaTemplateSearchTerm('');
                  setWabaTemplatesList(wabaTemplates);
                }
              }}
              options={wabaTemplatesList}
              getOptionLabel={(option) => option.tag || ''}
              value={wabaTemplatesList.find(t => t.tag === selectedTemplate) || null}
              onChange={(event, newValue) => {
                setSelectedTemplate(newValue?.tag || '');
              }}
              onInputChange={(event, value) => {
                debouncedSearchWabaTemplates(value);
              }}
              disableCloseOnSelect={true}
              loading={isLoadingWabaTemplates}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label={t('duplicate.selectTemplate')}
                  margin="normal"
                  required
                  InputProps={{
                    ...params.InputProps,
                    endAdornment: (
                      <>
                        {isLoadingWabaTemplates ? <CircularProgress color="inherit" size={20} /> : null}
                        {params.InputProps.endAdornment}
                      </>
                    ),
                  }}
                />
              )}
            />

            <TextField
              fullWidth
              label={t('disparador.dataEnvio')}
              margin="normal"
              type="datetime-local"
              value={duplicateDateTime.slice(0, 16)}
              onChange={(event) => {
                const selectedDate = new Date(event.target.value);
                setDuplicateDateTime(formatDate(selectedDate));
              }}
              InputLabelProps={{ shrink: true }}
              required
            />
            
            <Box display="flex" justifyContent="flex-end" gap="16px" mt={2}>
              <Button
                variant="outlined"
                onClick={() => onClose('waba')}
                sx={{
                  minWidth: '120px',
                  height: '36px',
                  borderColor: '#1976d2',
                  color: '#1976d2',
                  '&:hover': {
                    borderColor: '#1565c0',
                    backgroundColor: 'rgba(25, 118, 210, 0.04)',
                  },
                }}
              >
                {t('disparador.cancelar')}
              </Button>
              <Button
                variant="contained"
                onClick={onConfirmDuplicateWaba}
                disabled={isDuplicating || !selectedTemplate}
                sx={{
                  minWidth: '120px',
                  height: '36px',
                  backgroundColor: '#1976d2',
                  color: 'white',
                  '&:hover': {
                    backgroundColor: '#1565c0',
                  },
                }}
              >
                {isDuplicating ? (
                  <CircularProgress size={24} style={{ color: 'white' }} />
                ) : (
                  getButtonText()
                )}
              </Button>
            </Box>
          </div>
        </div>
      </Modal>

      {/* Modal para Duplicação BROKER */}
      <Modal
        open={duplicateBrokerModalOpen}
        onClose={() => onClose('broker')}
        aria-labelledby="modal-duplicate-broker-confirmation"
        aria-describedby="modal-duplicate-broker-confirmation-description"
      >
        <div className="modal">
          <Box
            className="modal-content"
            sx={{
              width: { xs: '95vw', sm: '90vw', md: '600px' },
              maxWidth: '90vw',
              maxHeight: { xs: '95vh', sm: '90vh', md: '80vh' },
              top: '50%',
              left: '50%',
              transform: 'translate(-50%, -50%)',
              borderRadius: '10px',
              backgroundColor: 'white',
              boxShadow: '0 4px 8px rgba(0, 0, 0, 0.2)',
              padding: { xs: '16px', sm: '20px' },
              position: 'absolute',
              overflow: 'auto',
            }}
          >
            <Box display="flex" justifyContent="center" alignItems="center" mb={2} position="relative">
              <Typography id="modal-duplicate-broker-confirmation" variant="h6" component="h2">
                {t('duplicate.title')}
              </Typography>
              <IconButton onClick={() => onClose('broker')} sx={{ position: 'absolute', right: 0 }}>
                <CloseIcon />
              </IconButton>
            </Box>
            
            <Typography variant="body1" mb={2}>
              {t('duplicate.configureData')}
            </Typography>
            
            <Typography variant="body2" color="textSecondary" mb={2} sx={{ fontStyle: 'italic' }}>
              {t('duplicate.leaveBlankTip')}
            </Typography>

            {/* Seleção de Mensagem Rápida */}
            <Autocomplete
              fullWidth
              margin="normal"
              open={brokerSearchOpen}
              onOpen={() => setBrokerSearchOpen(true)}
              onClose={() => {
                setBrokerSearchOpen(false);
                // Resetar o termo de busca quando fechar, retornando à lista original
                if (brokerSearchTerm.trim()) {
                  setBrokerSearchTerm('');
                  setBrokerMessagesList(brokerMensagensRapidas);
                }
              }}
              options={brokerMessagesList}
              getOptionLabel={(option) => option.tag || ''}
              value={brokerMessagesList.find(m => m.id === brokerMensagemRapida) || null}
              onChange={(event, newValue) => {
                setBrokerMensagemRapida(newValue?.id || '');
                if (newValue?.id) {
                  setBrokerMensagem('');
                }
              }}
              onInputChange={(event, value) => {
                debouncedSearchBrokerMessages(value);
              }}
              disableCloseOnSelect={true}
              loading={isLoadingBrokerMessages}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label={t('duplicate.selectQuickMessageOrCustom')}
                  margin="normal"
                  size="small"
                  InputProps={{
                    ...params.InputProps,
                    endAdornment: (
                      <>
                        {isLoadingBrokerMessages ? <CircularProgress color="inherit" size={20} /> : null}
                        {params.InputProps.endAdornment}
                      </>
                    ),
                  }}
                  MenuProps={{
                    PaperProps: {
                      style: {
                        maxHeight: 200,
                        maxWidth: 560,
                      }
                    },
                  }}
                />
              )}
            />

            {/* Campo de Mensagem Personalizada */}
            {!brokerMensagemRapida && (
              <TextField
                fullWidth
                label={t('duplicate.customMessage')}
                margin="normal"
                multiline
                rows={4}
                value={brokerMensagem}
                onChange={(e) => {
                  setBrokerMensagem(e.target.value);
                  if (e.target.value && brokerMensagemRapida) {
                    setBrokerMensagemRapida('');
                  }
                }}
                placeholder={t('duplicate.typeCustomMessage')}
              />
            )}

            {/* Upload de Imagem (apenas se não for mensagem rápida) */}
            {!brokerMensagemRapida && (
              <Box mt={2}>
                <Button variant="contained" component="label" disabled={isImageUploading}>
                  {isImageUploading ? t('duplicate.uploading') : t('duplicate.addImage')}
                  <input
                    type="file"
                    accept="image/jpeg, image/png, image/gif, application/pdf"
                    hidden
                    onChange={handleBrokerImageChange}
                  />
                </Button>
                {brokerImagemUrl && (
                  <Box mt={1}>
                    <Typography variant="caption" color="success.main">
                      ✓ {t('duplicate.imageAttached')}: {brokerImagem?.name}
                    </Typography>
                  </Box>
                )}
              </Box>
            )}



            {/* Data e Hora */}
            <TextField
              fullWidth
              label={t('disparador.dataEnvio')}
              margin="normal"
              type="datetime-local"
              value={duplicateDateTime.slice(0, 16)}
              onChange={(event) => {
                const selectedDate = new Date(event.target.value);
                setDuplicateDateTime(formatDate(selectedDate));
              }}
              InputLabelProps={{ shrink: true }}
              required
            />
            
            <Box 
              display="flex" 
              justifyContent="flex-end" 
              gap="16px" 
              mt={2}
              sx={{
                flexDirection: { xs: 'column', sm: 'row' },
                gap: { xs: '8px', sm: '16px' },
              }}
            >
              <Button
                variant="outlined"
                onClick={() => onClose('broker')}
                sx={{
                  minWidth: { xs: '100%', sm: '120px' },
                  height: '36px',
                  borderColor: '#1976d2',
                  color: '#1976d2',
                  '&:hover': {
                    borderColor: '#1565c0',
                    backgroundColor: 'rgba(25, 118, 210, 0.04)',
                  },
                }}
              >
                {t('disparador.cancelar')}
              </Button>
              <Button
                variant="contained"
                onClick={onConfirmDuplicateBroker}
                disabled={isDuplicating}
                sx={{
                  minWidth: { xs: '100%', sm: '120px' },
                  height: '36px',
                  backgroundColor: '#1976d2',
                  color: 'white',
                  '&:hover': {
                    backgroundColor: '#1565c0',
                  },
                }}
              >
                {isDuplicating ? (
                  <CircularProgress size={24} style={{ color: 'white' }} />
                ) : (
                  getButtonText()
                )}
              </Button>
            </Box>
          </Box>
        </div>
      </Modal>
    </>
  );
};

export default DuplicateModals;